import React, { useState } from 'react';
import { AlertTriangle, Clock, ChevronDown, TrendingDown, Ban } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DeductionEvent } from '../data/deductions';
function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
}
interface ActiveDeductionsProps {
  deductions: DeductionEvent[];
}
export function ActiveDeductions({
  deductions
}: ActiveDeductionsProps) {
  const [expanded, setExpanded] = useState(false);
  const DEDUCTIONS = deductions;
  const TOTAL_DEDUCTIONS = deductions.reduce((s, d) => s + d.points, 0);
  // Per feedback: only render if there are deductions
  if (DEDUCTIONS.length === 0) return null;
  return <motion.div initial={{
    opacity: 0,
    y: -10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden" data-id="element-15" data-mp="2u25ubbr8mgq6">
      <button onClick={() => setExpanded(!expanded)} className="w-full bg-red-50 hover:bg-red-100/70 transition-colors px-5 py-3 flex items-center justify-between gap-3" aria-expanded={expanded} data-id="element-16" data-mp="1cqdqd1glq67w">
        <div className="flex items-center gap-3 min-w-0" data-id="element-17" data-mp="1motj18va1nji">
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0" data-id="element-18" data-mp="1mm7c9bg7nc2c">
            <TrendingDown className="w-5 h-5 text-red-600" data-id="element-19" data-mp="oyylp21crcth" />
          </div>
          <div className="min-w-0 text-left" data-id="element-20" data-mp="u05soyvo87z3">
            <h3 className="text-sm font-extrabold text-red-800 uppercase tracking-wide leading-tight" data-id="element-21" data-mp="yz8veco6i23w">
              Active Deductions This Month
            </h3>
            <p className="text-xs text-red-600/80 leading-tight mt-0.5" data-id="element-22" data-mp="13rsaxf6ff7bc">
              {DEDUCTIONS.length} {DEDUCTIONS.length === 1 ? 'event' : 'events'}{' '}
              · {Math.abs(TOTAL_DEDUCTIONS).toLocaleString()} pts lost
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0" data-id="element-23" data-mp="37dy3vpb0w1e">
          <span className="text-2xl font-extrabold text-red-600 leading-none" data-id="element-24" data-mp="3vcrf8xpkk9fe">
            {TOTAL_DEDUCTIONS.toLocaleString()}
          </span>
          <ChevronDown className={`w-5 h-5 text-red-600 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-25" data-mp="cy0c5lhdhehz" />
        </div>
      </button>

      <AnimatePresence initial={false} data-id="element-26" data-mp="3gepa4j2w3fw1">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-27" data-mp="3gh1xjbrf2igh">
            <ul className="divide-y divide-gray-100" data-id="element-28" data-mp="15m4i883uvtl7">
              {DEDUCTIONS.map(d => <DeductionRow key={d.id} deduction={d} data-id="element-29" data-mp="10ovwxynev9fq" />)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
function DeductionRow({
  deduction
}: {
  deduction: DeductionEvent;
}) {
  const Icon = deduction.type === 'time_missed' ? Clock : deduction.type === 'withdrawn' ? Ban : AlertTriangle;
  return <li className="px-5 py-3 flex items-center justify-between gap-3" data-id="element-30" data-mp="2ejm70bkhfza4">
      <div className="flex items-center gap-3 min-w-0" data-id="element-31" data-mp="329ovyit3op4i">
        <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center shrink-0" data-id="element-32" data-mp="32b02gqdxkazb">
          <Icon className="w-4 h-4 text-red-500" data-id="element-33" data-mp="36a0i60142xi3" />
        </div>
        <div className="min-w-0" data-id="element-34" data-mp="2skf5f7dwneii">
          <p className="text-sm font-bold text-gray-800 leading-tight" data-id="element-35" data-mp="2sjds0w9f2fxm">
            {deduction.label}
          </p>
          <p className="text-xs text-gray-500 leading-tight mt-0.5 truncate" data-id="element-36" data-mp="2xh5lhuppma3x">
            {deduction.detail} · {formatDate(deduction.date)}
          </p>
        </div>
      </div>
      <span className="text-sm font-extrabold text-red-600 shrink-0" data-id="element-37" data-mp="3q02pyev8wy1q">
        {deduction.points} pts
      </span>
    </li>;
}